/**
 * Pitch Deck Forms Page
 * 
 * Contains both Form 1 (Business Basics) and Form 2 (Market & Model)
 * with tabbed navigation and progress tracking.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { Loader2, FileEdit, Info, CheckCircle2, Sparkles, RotateCcw } from "lucide-react";
import { PitchDeckAccessGuard } from "@/components/pitch-deck/PitchDeckAccessGuard";
import { PitchDeckForm1 } from "@/components/pitch-deck/PitchDeckForm1";
import { PitchDeckForm2 } from "@/components/pitch-deck/PitchDeckForm2";
import { PitchDeckFormProgress } from "@/components/pitch-deck/PitchDeckFormProgress";
import { ResetPitchDeckDialog } from "@/components/pitch-deck/ResetPitchDeckDialog";
import { usePitchDeckForm, Form1Data, Form2Data } from "@/hooks/usePitchDeckForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

function PitchDeckFormsContent() {
  const navigate = useNavigate();
  const {
    formData,
    updateField,
    errors,
    setErrors,
    isSaving,
    isLoading,
    lastSaved,
    hasUnsavedChanges,
    handleSave,
    handleReset,
    isForm1Complete,
    isForm2Complete,
  } = usePitchDeckForm();

  const [activeForm, setActiveForm] = useState<1 | 2>(1);
  const [isEditing, setIsEditing] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Set active form based on completion status on initial load
  useEffect(() => {
    if (!isLoading && isForm1Complete && !isForm2Complete) {
      setActiveForm(2);
    }
  }, [isLoading, isForm1Complete, isForm2Complete]);

  const handleForm1Continue = () => {
    setActiveForm(2);
  };

  const handleBackToForm1 = () => {
    setActiveForm(1);
  };

  // Handle form 2 completion - navigate to generate page
  const handleForm2Complete = useCallback(() => {
    toast({
      title: "Forms completed!",
      description: "Redirecting to pitch deck generator...",
    });
    // Small delay so user sees the success message
    setTimeout(() => {
      navigate("/pitch-deck/generate");
    }, 800);
  }, [navigate]);

  // Handle reset confirmation
  const handleConfirmReset = useCallback(async () => {
    setIsResetting(true);
    const success = await handleReset();
    setIsResetting(false);
    setResetDialogOpen(false);
    
    if (success) {
      setIsEditing(false);
      setActiveForm(1);
      toast({
        title: "Reset complete",
        description: "All pitch deck data has been cleared. You can start fresh!",
      });
    } else {
      toast({
        title: "Reset failed",
        description: "There was an error resetting your data. Please try again.",
        variant: "destructive",
      });
    }
  }, [handleReset]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your form data...</p>
        </div>
      </div>
    );
  }

  // Handle edit button click
  const handleEditResponses = () => {
    setIsEditing(true);
    setActiveForm(1);
  };

  // Success state when both forms are complete (and not editing)
  if (isForm2Complete && !isEditing) {
    return (
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        <Card className="shadow-lg">
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-6">
              <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-primary" />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  ✓ Forms Complete!
                </h2>
                <p className="text-muted-foreground">
                  You're ready to generate your professional pitch deck.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="ghost"
                  onClick={() => setResetDialogOpen(true)}
                  className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <RotateCcw className="h-4 w-4" />
                  Start Over
                </Button>
                <Button
                  variant="outline"
                  onClick={handleEditResponses}
                  className="gap-2"
                >
                  <FileEdit className="h-4 w-4" />
                  Edit Your Responses
                </Button>
                <Button 
                  onClick={() => navigate("/pitch-deck/generate")}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Generate Pitch Deck
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <ResetPitchDeckDialog
          open={resetDialogOpen}
          onOpenChange={setResetDialogOpen}
          onConfirm={handleConfirmReset}
          isResetting={isResetting}
        />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto">
      {/* Progress Section */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <PitchDeckFormProgress
            activeForm={activeForm}
            isForm1Complete={isForm1Complete}
            isForm2Complete={isForm2Complete}
            onFormChange={setActiveForm}
          />
        </CardContent>
      </Card>

      {/* Info Alert for first-time users */}
      {!isForm1Complete && activeForm === 1 && (
        <Alert className="mb-6 bg-primary/5 border-primary/20">
          <Info className="h-4 w-4 text-primary" />
          <AlertDescription className="text-foreground">
            📝 Complete this form to provide the basic information for your pitch deck.
            Your progress is saved automatically every 10 seconds.
          </AlertDescription>
        </Alert>
      )}

      {activeForm === 2 && !isForm2Complete && (
        <Alert className="mb-6 bg-primary/5 border-primary/20">
          <Info className="h-4 w-4 text-primary" />
          <AlertDescription className="text-foreground">
            📊 Now tell us about your market and business model.
            This information will help create compelling slides for investors.
          </AlertDescription>
        </Alert>
      )}

      {/* Form Card */}
      <Card className="shadow-lg">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FileEdit className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>
                  {activeForm === 1 ? "Form 1: Business Basics" : "Form 2: Market & Business Model"}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {activeForm === 1
                    ? "Tell us about your business and team"
                    : "Describe your market and business model"}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setResetDialogOpen(true)}
              className="gap-2 text-muted-foreground hover:text-destructive"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="hidden sm:inline">Start Over</span>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {activeForm === 1 ? (
            <PitchDeckForm1
              formData={formData as Form1Data}
              updateField={updateField}
              errors={errors}
              setErrors={setErrors}
              onSave={handleSave}
              onContinue={handleForm1Continue}
              isSaving={isSaving}
              lastSaved={lastSaved}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          ) : (
            <PitchDeckForm2
              formData={formData as Form2Data}
              updateField={updateField}
              errors={errors}
              setErrors={setErrors}
              onSave={handleSave}
              onBack={handleBackToForm1}
              onComplete={handleForm2Complete}
              isSaving={isSaving}
              lastSaved={lastSaved}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          )}
        </CardContent>
      </Card>

      <ResetPitchDeckDialog
        open={resetDialogOpen}
        onOpenChange={setResetDialogOpen}
        onConfirm={handleConfirmReset}
        isResetting={isResetting}
      />
    </div>
  );
}

export default function PitchDeckForms() {
  return (
    <PitchDeckAccessGuard>
      <PitchDeckFormsContent />
    </PitchDeckAccessGuard>
  );
}
